// src/utils/refunds.js
// Refund vocabulary and form helpers shared by the refunds page, the refund
// detail and the ticket tab. Mirrors app/services/refund_service.py (KI-158):
// add a status / settlement there and here together.

export const REFUND_STATUSES = ['requested', 'approved', 'processing', 'paid', 'rejected', 'cancelled'];
export const REFUND_SOURCES = ['carrier', 'warehouse', 'logistician', 'manual'];
export const REFUND_REASONS = ['lost_parcel', 'delayed_delivery', 'damaged_parcel', 'missing_content', 'other'];
export const REFUND_SETTLEMENTS = ['unknown', 'direct_payment', 'monthly_credit', 'credit_note', 'invoice_deduction'];
export const GRANTED_STATUSES = ['approved', 'processing', 'paid'];

export const REFUND_STATUS_DOT = {
  requested: 'var(--blue)',
  approved: 'var(--brand-accent)',
  processing: 'var(--violet)',
  paid: 'var(--state-success)',
  rejected: 'var(--state-danger)',
  cancelled: 'var(--text-muted)',
};

const AMOUNT_FIELDS = ['shipping_cost_refund_amount', 'content_refund_amount', 'insurance_refund_amount'];

const toInput = (value) => (value == null || Number(value) === 0 ? '' : String(value));
const toNumberOrNull = (value) => (value === '' || value == null ? null : Number(value));
const toAmount = (value) => (value === '' || value == null ? 0 : Number(value));
const textOrNull = (value) => {
  const text = (value ?? '').toString().trim();
  return text ? text : null;
};

// A blank form for a new refund, prefilled from the ticket's lookup context.
export const newRefundForm = (prefill) => {
  const declared = prefill?.declared || {};
  return {
    source: 'carrier',
    reason: 'lost_parcel',
    status: 'approved',
    settlement: 'unknown',
    shipping_cost_refund_amount: '',
    content_refund_amount: '',
    insurance_refund_amount: '',
    has_insurance: Number(declared.insurance_value || 0) > 0,
    insurance_value: toInput(declared.insurance_value),
    insurance_price: '',
    currency: declared.currency || 'EUR',
    carrier_reference: '',
    order_reference: '',
    note: '',
  };
};

export const refundFormFromRefund = (refund) => ({
  source: refund.source || 'carrier',
  reason: refund.reason || 'lost_parcel',
  status: refund.status || 'requested',
  settlement: refund.settlement || 'unknown',
  shipping_cost_refund_amount: toInput(refund.shipping_cost_refund_amount),
  content_refund_amount: toInput(refund.content_refund_amount),
  insurance_refund_amount: toInput(refund.insurance_refund_amount),
  has_insurance: Boolean(refund.has_insurance),
  insurance_value: toInput(refund.insurance_value),
  insurance_price: toInput(refund.insurance_price),
  currency: refund.currency || 'EUR',
  carrier_reference: refund.carrier_reference || '',
  order_reference: refund.order_reference || '',
  note: refund.note || '',
});

// API payload. An empty order reference on creation means « the ticket's
// order »; on an edit the form holds the refund's own value.
export const refundPayload = (form) => ({
  source: form.source,
  reason: form.reason,
  status: form.status,
  settlement: form.settlement || 'unknown',
  shipping_cost_refund_amount: toAmount(form.shipping_cost_refund_amount),
  content_refund_amount: toAmount(form.content_refund_amount),
  insurance_refund_amount: toAmount(form.insurance_refund_amount),
  has_insurance: Boolean(form.has_insurance),
  insurance_value: toNumberOrNull(form.insurance_value),
  insurance_price: toNumberOrNull(form.insurance_price),
  currency: (form.currency || 'EUR').toUpperCase(),
  carrier_reference: textOrNull(form.carrier_reference),
  order_reference: textOrNull(form.order_reference),
  note: textOrNull(form.note),
});

export const formatRefundMoney = (amount, currency = 'EUR', lang = 'fr') =>
  `${Number(amount || 0).toLocaleString(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || 'EUR'}`;

// Granted but the carrier has not given the amount yet.
export const refundAmountPending = (refund) => {
  const total = Number(refund?.total_refund_amount || 0);
  if (total > 0) return false;
  return refund?.amount_known === false || GRANTED_STATUSES.includes(refund?.status);
};

export const refundAmountParts = (refund) =>
  AMOUNT_FIELDS.filter((key) => Number(refund?.[key] || 0) > 0).map((key) => ({ key, value: Number(refund[key]) }));
